/**
 * Slow Queries Tab Component
 * Displays slow queries from pg_stat_statements with optimized rendering
 */

import { useState, useMemo } from 'react';
import { useSlowQueries } from '../../../hooks/admin';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { AlertCircle, RefreshCw, Search, ArrowUpDown, ArrowUp, ArrowDown, FileText } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import type { SlowQuery } from '../../../hooks/admin/types/database';
import { ExplainPlanModal } from './ExplainPlanModal';

type SortField = 'avg_time_ms' | 'calls' | 'max_time_ms' | 'total_time';
type SortDirection = 'asc' | 'desc';

export function SlowQueriesTab() {
  const [hours, setHours] = useState(24);
  const [threshold, setThreshold] = useState(1000);
  const [limit, setLimit] = useState(50);
  const [searchTerm, setSearchTerm] = useState('');
  const [queryTypeFilter, setQueryTypeFilter] = useState<string>('all');
  const [sortField, setSortField] = useState<SortField>('avg_time_ms');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  // State for Explain Plan modal
  const [explainModalOpen, setExplainModalOpen] = useState(false);
  const [selectedQuery, setSelectedQuery] = useState<string>('');

  const { data, isLoading, isError, mutate } = useSlowQueries({
    hours: hours,
    threshold_ms: threshold,
    limit: limit,
  });

  // Optimized filtering and sorting using useMemo to prevent re-computation on every render
  const filteredAndSortedQueries = useMemo(() => {
    if (!data?.queries) return [];

    let result = [...data.queries];

    // Filter by search term
    if (searchTerm) {
      const lowerSearch = searchTerm.toLowerCase();
      result = result.filter(q =>
        q.query.toLowerCase().includes(lowerSearch)
      );
    }

    // Filter by query type
    if (queryTypeFilter !== 'all') {
      result = result.filter(q => {
        const query = q.query.toUpperCase().trim();
        return query.startsWith(queryTypeFilter.toUpperCase());
      });
    }

    // Sort queries
    result.sort((a, b) => {
      let aValue: number;
      let bValue: number;

      switch (sortField) {
        case 'avg_time_ms':
          aValue = a.avg_time_ms;
          bValue = b.avg_time_ms;
          break;
        case 'calls':
          aValue = a.calls;
          bValue = b.calls;
          break;
        case 'max_time_ms':
          aValue = a.max_time_ms;
          bValue = b.max_time_ms;
          break;
        case 'total_time':
          aValue = a.avg_time_ms * a.calls;
          bValue = b.avg_time_ms * b.calls;
          break;
      }

      return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
    });

    return result;
  }, [data?.queries, searchTerm, queryTypeFilter, sortField, sortDirection]);

  const handleRefresh = () => {
    mutate();
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const getSortIcon = (field: SortField) => {
    if (sortField !== field) {
      return <ArrowUpDown className="h-3 w-3 ml-1 inline" />;
    }
    return sortDirection === 'asc' ? (
      <ArrowUp className="h-3 w-3 ml-1 inline" />
    ) : (
      <ArrowDown className="h-3 w-3 ml-1 inline" />
    );
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (isError) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertTitle>Error</AlertTitle>
        <AlertDescription>
          Failed to load slow queries. Please check if pg_stat_statements extension is enabled.
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-4">
      {/* Controls */}
      <Card>
        <CardHeader>
          <CardTitle>Query Filters</CardTitle>
          <CardDescription>
            Configure threshold and limit for slow query detection
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            {/* Time Window Selector */}
            <div className="space-y-2">
              <label htmlFor="hours-select" className="text-sm font-medium">Time Window</label>
              <select
                id="hours-select"
                value={hours}
                onChange={(e) => setHours(Number(e.target.value))}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <option value="1">1 hour</option>
                <option value="6">6 hours</option>
                <option value="24">24 hours</option>
                <option value="72">3 days</option>
                <option value="168">7 days</option>
              </select>
            </div>

            {/* Threshold Selector */}
            <div className="space-y-2">
              <label htmlFor="threshold-select" className="text-sm font-medium">Threshold (ms)</label>
              <select
                id="threshold-select"
                value={threshold}
                onChange={(e) => setThreshold(Number(e.target.value))}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <option value="100">100ms</option>
                <option value="500">500ms</option>
                <option value="1000">1000ms</option>
                <option value="2000">2000ms</option>
                <option value="5000">5000ms</option>
              </select>
            </div>

            {/* Query Type Filter */}
            <div className="space-y-2">
              <label htmlFor="query-type-select" className="text-sm font-medium">Query Type</label>
              <select
                id="query-type-select"
                value={queryTypeFilter}
                onChange={(e) => setQueryTypeFilter(e.target.value)}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <option value="all">All Types</option>
                <option value="select">SELECT</option>
                <option value="insert">INSERT</option>
                <option value="update">UPDATE</option>
                <option value="delete">DELETE</option>
              </select>
            </div>

            {/* Limit Selector */}
            <div className="space-y-2">
              <label htmlFor="limit-select" className="text-sm font-medium">Limit</label>
              <select
                id="limit-select"
                value={limit}
                onChange={(e) => setLimit(Number(e.target.value))}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <option value="10">10</option>
                <option value="25">25</option>
                <option value="50">50</option>
                <option value="100">100</option>
                <option value="200">200</option>
              </select>
            </div>

            {/* Search Box */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Search Query Text</label>
              <div className="relative">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Filter by query text..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-8"
                />
              </div>
            </div>
          </div>

          {/* Sort Controls */}
          <div className="flex flex-wrap items-center gap-2 mt-4 pt-4 border-t">
            <span className="text-sm font-medium mr-2">Sort by:</span>
            <Button
              variant={sortField === 'avg_time_ms' ? 'default' : 'outline'}
              size="sm"
              onClick={() => handleSort('avg_time_ms')}
              className="gap-1"
            >
              Avg Time
              {getSortIcon('avg_time_ms')}
            </Button>
            <Button
              variant={sortField === 'calls' ? 'default' : 'outline'}
              size="sm"
              onClick={() => handleSort('calls')}
              className="gap-1"
            >
              Calls
              {getSortIcon('calls')}
            </Button>
            <Button
              variant={sortField === 'max_time_ms' ? 'default' : 'outline'}
              size="sm"
              onClick={() => handleSort('max_time_ms')}
              className="gap-1"
            >
              Max Time
              {getSortIcon('max_time_ms')}
            </Button>
            <Button
              variant={sortField === 'total_time' ? 'default' : 'outline'}
              size="sm"
              onClick={() => handleSort('total_time')}
              className="gap-1"
            >
              Total Time
              {getSortIcon('total_time')}
            </Button>
          </div>

          <div className="flex justify-between items-center mt-4">
            <div className="text-sm text-muted-foreground">
              Showing {filteredAndSortedQueries.length} of {data?.total_count || 0} queries
              {data?.note && ` • ${data.note}`}
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={handleRefresh}
              className="gap-2"
            >
              <RefreshCw className="h-4 w-4" />
              Refresh
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Queries List */}
      {filteredAndSortedQueries.length === 0 ? (
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>No Slow Queries Found</AlertTitle>
          <AlertDescription>
            {searchTerm || queryTypeFilter !== 'all'
              ? 'No queries match your search criteria. Try adjusting your filters.'
              : `No queries found with average execution time > ${threshold}ms. This is good news!`
            }
          </AlertDescription>
        </Alert>
      ) : (
        <div className="space-y-3">
          {filteredAndSortedQueries.map((query, index) => (
            <Card key={index} className="hover:bg-accent/50 transition-colors">
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-2">
                      <Badge variant={query.avg_time_ms > 5000 ? 'destructive' : query.avg_time_ms > 2000 ? 'default' : 'secondary'}>
                        {query.avg_time_ms.toFixed(2)}ms avg
                      </Badge>
                      <Badge variant="outline">
                        {query.calls.toLocaleString()} calls
                      </Badge>
                      <Badge variant="outline">
                        {query.max_time_ms.toFixed(2)}ms max
                      </Badge>
                    </div>
                    <pre className="text-sm font-mono bg-muted p-3 rounded-md overflow-x-auto whitespace-pre-wrap break-all">
                      {query.query}
                    </pre>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="pt-0">
                <div className="flex items-end justify-between gap-4">
                  <div className="grid grid-cols-3 gap-4 text-sm flex-1">
                    <div>
                      <div className="text-muted-foreground">Std Deviation</div>
                      <div className="font-medium">{query.stddev_time_ms.toFixed(2)}ms</div>
                    </div>
                    <div>
                      <div className="text-muted-foreground">Rows Affected</div>
                      <div className="font-medium">{query.rows?.toLocaleString() || 'N/A'}</div>
                    </div>
                    <div>
                      <div className="text-muted-foreground">Total Time</div>
                      <div className="font-medium">
                        {((query.avg_time_ms * query.calls) / 1000).toFixed(2)}s
                      </div>
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setSelectedQuery(query.query);
                      setExplainModalOpen(true);
                    }}
                    className="gap-2 shrink-0"
                  >
                    <FileText className="h-4 w-4" />
                    View Plan
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Explain Plan Modal */}
      <ExplainPlanModal
        query={selectedQuery}
        open={explainModalOpen}
        onOpenChange={setExplainModalOpen}
      />
    </div>
  );
}
